import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, CheckIcon, XMarkIcon, TrashIcon, PlusIcon, UserGroupIcon, GlobeAltIcon, SignalIcon, Squares2X2Icon } from '@heroicons/react/24/outline';
import api from '../services/api';
import { registroGuardado } from '../utils/savedRecord';
import DocSmartButton from './DocSmartButton';
import CiActivityPanel from './common/CiActivityPanel';
import ContextTabLink from './common/ContextTabLink';
import CiSelector from './CiSelector';
import ServiceOverviewMap from './ServiceOverviewMap';
import '../styles/DetailView.css';
import TabButton from './TabButton';
import DetailCrumb from './DetailCrumb';
import FormDirtyDock from './common/FormDirtyDock';

const EMPTY = {
  name: '',
  service_id: '',
  farm_type: 'other',
  fqdn: '',
  functional_level: '',
  status: 'active',
  description: '',
  notes: '',
};

export default function ServiceFarmDetail({ farm, companyId, meta = {}, services = [], onClose, onSaved, initialMode }) {
  const isEdit = Boolean(farm?.id);
  const navigate = useNavigate();
  /* La ficha se abre EN LECTURA; 'create' y 'edit' editan. */
  const [editing, setEditing] = useState(initialMode ? initialMode !== 'view' : true);
  const [form, setForm] = useState(EMPTY);
  const [members, setMembers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [error, setError] = useState('');
  const [memberForm, setMemberForm] = useState({ server_id: '', vm_id: '', role: '', is_primary: false, priority: '', status: 'online', notes: '' });
  const [memberSaving, setMemberSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(farm?.id && initialMode === 'view' ? 'resumen' : 'general');

  useEffect(() => {
    if (farm) {
      setForm({
        name: farm.name || '',
        service_id: farm.service_id || '',
        farm_type: farm.farm_type || 'other',
        fqdn: farm.fqdn || '',
        functional_level: farm.functional_level || '',
        status: farm.status || 'active',
        description: farm.description || '',
        notes: farm.notes || '',
      });
      setMembers(farm.members || []);
    } else {
      setForm(EMPTY);
      setMembers([]);
    }
    setIsDirty(false);
  }, [farm, reloadKey]);

  useEffect(() => {
    if (farm?.id && initialMode === 'view') setActiveTab('resumen');
  }, [farm?.id, initialMode]);

  useEffect(() => {
    if (!isEdit || !companyId) return;
    api.getServiceFarm(farm.id, companyId).then(fresh => {
      setForm({
        name: fresh.name || '',
        service_id: fresh.service_id || '',
        farm_type: fresh.farm_type || 'other',
        fqdn: fresh.fqdn || '',
        functional_level: fresh.functional_level || '',
        status: fresh.status || 'active',
        description: fresh.description || '',
        notes: fresh.notes || '',
      });
      setMembers(fresh.members || []);
    }).catch(() => {});
  }, [isEdit, companyId, farm?.id, reloadKey]);

  const farmTypeRoles = useMemo(() => {
    const ft = (meta.farm_types || []).find(t => t.value === form.farm_type);
    return ft?.roles || [];
  }, [meta.farm_types, form.farm_type]);
  const farmTypeLabel = (meta.farm_types || []).find(t => t.value === form.farm_type)?.label || form.farm_type || '—';
  const parentService = services.find(s => Number(s.id) === Number(form.service_id));
  const parentServiceName = parentService?.display_name || parentService?.name
    || farm?.service_name || (form.service_id ? `Servicio #${form.service_id}` : 'Sin servicio padre');
  const membersOnline = members.filter(m => m.status === 'online').length;
  const primaryMember = members.find(m => m.is_primary);
  const memberName = (m) => m.ci_name || m.server_name || m.vm_name
    || (m.server_id ? `Servidor #${m.server_id}` : (m.vm_id ? `VM #${m.vm_id}` : (m.ci_id ? `CI #${m.ci_id}` : '—')));
  const memberRoute = (m) => {
    if (m.server_id) return `/infrastructure/servers?destacar=${m.server_id}`;
    if (m.vm_id) return `/virt/vms?destacar=${m.vm_id}`;
    if (m.ci_type === 'rack_element' && m.ci_id) return `/infrastructure/servers?destacar=${m.ci_id}`;
    if (m.ci_type === 'virt_vm' && m.ci_id) return `/virt/vms?destacar=${m.ci_id}`;
    return null;
  };
  const statusLabel = (s) => ({ active: 'Activa', degraded: 'Degradada', offline: 'Caída', online: 'Online', maintenance: 'Mantenimiento' }[s] || s || '—');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setIsDirty(true);
    setError('');
  };

  /* Descartar: recarga los valores guardados y sale de edición. */
  const handleDiscard = () => {
    setReloadKey(k => k + 1);
    setIsDirty(false);
    setError('');
    if (isEdit) setEditing(false);
  };

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = { ...form };
      if (!payload.name.trim()) throw new Error('El nombre es obligatorio');
      if (!payload.service_id) throw new Error('El servicio padre es obligatorio');
      payload.service_id = Number(payload.service_id);
      let creado = null;
      if (isEdit) await api.updateServiceFarm(farm.id, payload, companyId);
      else creado = registroGuardado(await api.createServiceFarm(payload, companyId));
      // Alta: el listado adopta el registro creado y la ficha sigue abierta ya
      // con id, así se encienden las zonas que necesitan estar guardado.
      onSaved?.(creado);
      setIsDirty(false);
    } catch (err) {
      setError(err.message || 'No se pudo guardar la granja');
    } finally {
      setSaving(false);
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!isEdit) return;
    if (!memberForm.server_id && !memberForm.vm_id) {
      setError('Indica server_id o vm_id para el miembro');
      return;
    }
    setMemberSaving(true);
    try {
      const payload = {
        ...memberForm,
        server_id: memberForm.server_id || null,
        vm_id: memberForm.vm_id || null,
        priority: memberForm.priority || null,
      };
      const created = await api.addServiceFarmMember(farm.id, payload, companyId);
      setMembers(prev => [...prev, created]);
      setMemberForm({ server_id: '', vm_id: '', role: '', is_primary: false, priority: '', status: 'online', notes: '' });
    } catch (err) {
      setError(err.message || 'No se pudo añadir el miembro');
    } finally {
      setMemberSaving(false);
    }
  };

  const handleDeleteMember = async (mid) => {
    if (!window.confirm('¿Eliminar este miembro?')) return;
    try {
      await api.deleteServiceFarmMember(farm.id, mid, companyId);
      setMembers(prev => prev.filter(m => m.id !== mid));
    } catch (err) {
      setError(err.message || 'No se pudo eliminar el miembro');
    }
  };

  return (
    <div className="dv-overlay">
      <div className="dv-topbar">
        <button className="dv-back-btn" type="button" onClick={onClose}>
          <ArrowLeftIcon />
          Granjas / AD
        </button>
        <DetailCrumb label={form.name || 'Nueva'} />
        <div className="dv-topbar-spacer" />
        {error && <span style={{ color: '#dc2626', fontSize: '0.85rem' }}>⚠ {error}</span>}
        <div className="dv-topbar-actions">
          {!editing ? (
            <>
              <button className="dv-btn dv-btn-secondary" type="button" onClick={onClose}>
                <XMarkIcon />
                Cerrar
              </button>
              <button className="dv-btn dv-btn-primary" type="button" onClick={() => { setEditing(true); setActiveTab('general'); }}>
                <CheckIcon />
                Editar
              </button>
            </>
          ) : (
            <>
              <button className="dv-btn dv-btn-secondary" type="button" onClick={onClose} disabled={saving}>
                <XMarkIcon />
                Descartar
              </button>
              <button className="dv-btn dv-btn-primary" type="button" onClick={handleSave} disabled={saving}>
                {saving ? <span className="dv-spinner" /> : <CheckIcon />}
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="dv-smartbar">
        {isEdit && (
          <button type="button" className="dv-smart-btn" onClick={() => setActiveTab('miembros')}>
            <div className="dv-smart-btn-icon"><UserGroupIcon /></div>
            <div className="dv-smart-btn-value">{members.length}</div>
            <div className="dv-smart-btn-label">Miembros</div>
          </button>
        )}
        <button type="button" className="dv-smart-btn" onClick={() => setActiveTab('general')}>
          <div className="dv-smart-btn-icon"><GlobeAltIcon /></div>
          <div className="dv-smart-btn-value">{form.fqdn || '—'}</div>
          <div className="dv-smart-btn-label">FQDN</div>
        </button>
        {isEdit && (
          <button type="button" className="dv-smart-btn" onClick={() => setActiveTab('miembros')}>
            <div className="dv-smart-btn-icon"><SignalIcon /></div>
            <div className="dv-smart-btn-value">{membersOnline} / {members.length}</div>
            <div className="dv-smart-btn-label">Online</div>
          </button>
        )}
        {isEdit && (
          <button type="button" className="dv-smart-btn" onClick={() => setActiveTab('dependencias')}
            title="Servicio padre y dependencias">
            <div className="dv-smart-btn-icon"><Squares2X2Icon /></div>
            <div className="dv-smart-btn-value">{form.service_id ? '🔗' : '—'}</div>
            <div className="dv-smart-btn-label">Dependencias</div>
          </button>
        )}

        {isEdit && (
          <DocSmartButton elementType="service_farm" elementId={farm.id} elementName={form.name} companyId={companyId} />
        )}
      </div>

      {/* ── Body ── (en lectura se apaga de golpe con el fieldset) ── */}
      <div className="dv-body">
        <div className="dv-main">
        <fieldset className="dv-fieldset" disabled={!editing}>
        <form className="dv-form" onSubmit={handleSave}>
          <input
            className="dv-record-title"
            name="name"
            value={form.name}
            onChange={handleChange}
            placeholder="Nombre de la granja / dominio"
            autoFocus
          />

          <div className="dv-tabs-nav">
            {isEdit && (
              <TabButton type="button" className={`dv-tab-btn${activeTab === 'resumen' ? ' active' : ''}`} onClick={() => setActiveTab('resumen')}>Resumen</TabButton>
            )}
            <TabButton type="button" className={`dv-tab-btn${activeTab === 'general' ? ' active' : ''}`} onClick={() => setActiveTab('general')}>General</TabButton>
            {isEdit && (
              <TabButton type="button" className={`dv-tab-btn${activeTab === 'miembros' ? ' active' : ''}`} onClick={() => setActiveTab('miembros')}>Miembros ({members.length})</TabButton>
            )}
            {isEdit && form.service_id && (
              <TabButton type="button" className={`dv-tab-btn${activeTab === 'dependencias' ? ' active' : ''}`} onClick={() => setActiveTab('dependencias')}>Dependencias</TabButton>
            )}
            <TabButton type="button" className={`dv-tab-btn${activeTab === 'notas' ? ' active' : ''}`} onClick={() => setActiveTab('notas')}>Descripción y notas</TabButton>
          </div>

          {activeTab === 'resumen' && isEdit && (
            <div className="dv-tab-pane">
              <div className="dv-context-grid">
                <div className="dv-context-card">
                  <div className="dv-context-icon">🧩</div>
                  <div className="dv-context-body">
                    <div className="dv-context-kicker">Servicio padre</div>
                    <div className="dv-context-title">{parentServiceName}</div>
                    <div className="dv-context-sub">{farmTypeLabel}</div>
                    {form.service_id && (
                      <div className="dv-context-actions">
                        <Link to={`/services?destacar=${form.service_id}&abrir=1`} className="dv-context-link">Abrir servicio ↗</Link>
                        <Link to={`/services/designer?service=${form.service_id}`} className="dv-context-link">Diseñador ↗</Link>
                      </div>
                    )}
                  </div>
                </div>

                <div className="dv-context-card">
                  <div className="dv-context-icon">🖥️</div>
                  <div className="dv-context-body">
                    <div className="dv-context-kicker">Miembros</div>
                    <div className="dv-context-title">{members.length} nodo{members.length === 1 ? '' : 's'}</div>
                    <div className="dv-context-sub">{membersOnline} online · {members.length - membersOnline} fuera de línea</div>
                    <ul className="dv-context-list">
                      {members.slice(0, 4).map(m => {
                        const route = memberRoute(m);
                        return (
                          <li key={m.id}>
                            {route ? <Link to={route} className="dv-context-link">{memberName(m)}</Link> : memberName(m)}
                            {m.is_primary ? ' · primario' : ''}
                          </li>
                        );
                      })}
                      {members.length > 4 && <li>+{members.length - 4} más…</li>}
                    </ul>
                    <div className="dv-context-actions">
                      <ContextTabLink tab="miembros" onSelect={setActiveTab}>Ver miembros</ContextTabLink>
                    </div>
                  </div>
                </div>

                <div className="dv-context-card">
                  <div className="dv-context-icon">🌐</div>
                  <div className="dv-context-body">
                    <div className="dv-context-kicker">Granja</div>
                    <div className="dv-context-title">{form.fqdn || 'Sin FQDN'}</div>
                    <div className="dv-context-sub">{farmTypeLabel} · nivel {form.functional_level || '—'} · {statusLabel(form.status)}</div>
                    <div className="dv-context-actions">
                      <ContextTabLink tab="general" onSelect={setActiveTab}>Ver configuración</ContextTabLink>
                    </div>
                  </div>
                </div>

                <div className="dv-context-card dv-context-card-warn">
                  <div className="dv-context-icon">⚠️</div>
                  <div className="dv-context-body">
                    <div className="dv-context-kicker">Impacto</div>
                    <div className="dv-context-title">
                      {members.length ? `${members.length} nodo${members.length === 1 ? '' : 's'} y ${parentServiceName}` : 'Sin miembros'}
                    </div>
                    <div className="dv-context-sub">Si la granja cae, el servicio padre y sus dependencias quedan afectados.</div>
                    <div className="dv-context-actions">
                      {form.service_id && (
                        <ContextTabLink tab="dependencias" onSelect={setActiveTab}>Ver dependencias ↗</ContextTabLink>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="dv-section">
                <div className="dv-section-title">Resumen de la granja</div>
                <div className="dv-summary-grid">
                  <div><span>Tipo</span><strong>{farmTypeLabel}</strong></div>
                  <div><span>FQDN</span><strong>{form.fqdn || '—'}</strong></div>
                  <div><span>Miembros online</span><strong>{membersOnline} / {members.length}</strong></div>
                  <div><span>Primario</span><strong>{primaryMember ? memberName(primaryMember) : '—'}</strong></div>
                  <div><span>Estado</span><strong>{statusLabel(form.status)}</strong></div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'general' && (
            <div className="dv-tab-pane">
              <div className="dv-section">
                <div className="dv-section-title">Configuración</div>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Servicio padre <span className="text-danger">*</span></label>
                    <select name="service_id" value={form.service_id} onChange={handleChange} className="form-select" required>
                      <option value="">Selecciona servicio</option>
                      {services.map(s => <option key={s.id} value={s.id}>{s.display_name || s.name}</option>)}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Tipo de granja</label>
                    <select name="farm_type" value={form.farm_type} onChange={handleChange} className="form-select">
                      {(meta.farm_types || []).map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Estado</label>
                    <select name="status" value={form.status} onChange={handleChange} className="form-select">
                      {(meta.statuses || ['active','degraded','offline']).map(s => <option key={s} value={s}>{statusLabel(s)}</option>)}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">FQDN / dominio</label>
                    <input name="fqdn" value={form.fqdn} onChange={handleChange} className="form-control" placeholder="empresa.local" />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Nivel funcional</label>
                    <input name="functional_level" value={form.functional_level} onChange={handleChange} className="form-control" placeholder="2019" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'notas' && (
            <div className="dv-tab-pane">
              <div className="dv-section">
                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label">Descripción</label>
                    <textarea name="description" value={form.description} onChange={handleChange} className="form-control" rows={3} />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Notas</label>
                    <textarea name="notes" value={form.notes} onChange={handleChange} className="form-control" rows={3} placeholder="Notas operativas de la granja…" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </form>

          {activeTab === 'miembros' && isEdit && (
            <div className="dv-tab-pane">
              <div className="dv-section">
                <div className="dv-section-title">Miembros</div>
                {members.length === 0 && <div className="dv-empty">Sin miembros todavía</div>}
                {members.length > 0 && (
                  <div className="dv-table-wrap">
                    <table className="dv-table">
                      <thead>
                        <tr>
                          <th>Rol</th>
                          <th>Host</th>
                          <th>Estado</th>
                          <th>Primario</th>
                          <th>Notas</th>
                          {editing && <th></th>}
                        </tr>
                      </thead>
                      <tbody>
                        {members.map(m => {
                          const route = memberRoute(m);
                          return (
                            <tr key={m.id}>
                              <td>{m.role || '—'}</td>
                              <td>
                                {route ? (
                                  <Link to={route} className="dv-link">{memberName(m)}</Link>
                                ) : memberName(m)}
                              </td>
                              <td>
                                <span className={`dv-status-badge ${m.status === 'online' ? 'dv-status-active' : 'dv-status-inactive'}`}>
                                  {statusLabel(m.status)}
                                </span>
                              </td>
                              <td>{m.is_primary ? 'Sí' : '—'}</td>
                              <td>{m.notes || '—'}</td>
                              {editing && (
                                <td>
                                  <button className="dv-icon-btn" type="button" onClick={() => handleDeleteMember(m.id)} title="Eliminar">
                                    <TrashIcon />
                                  </button>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              {editing && (
                <div className="dv-section">
                  <div className="dv-section-title">Añadir miembro</div>
                  <form className="dv-field-grid" onSubmit={handleAddMember}>
                    <div className="dv-field">
                      <span>Servidor físico</span>
                      <CiSelector
                        ciType="rack_element"
                        ciId={memberForm.server_id}
                        companyId={companyId}
                        fixedType="rack_element"
                        onSelect={(ci) => setMemberForm(f => ({ ...f, server_id: ci.ci_id, vm_id: '' }))}
                        onClear={() => setMemberForm(f => ({ ...f, server_id: '' }))}
                      />
                    </div>
                    <div className="dv-field">
                      <span>Máquina virtual</span>
                      <CiSelector
                        ciType="virt_vm"
                        ciId={memberForm.vm_id}
                        companyId={companyId}
                        fixedType="virt_vm"
                        onSelect={(ci) => setMemberForm(f => ({ ...f, vm_id: ci.ci_id, server_id: '' }))}
                        onClear={() => setMemberForm(f => ({ ...f, vm_id: '' }))}
                      />
                    </div>
                    <label className="dv-field">
                      <span>Rol</span>
                      <select value={memberForm.role} onChange={e => setMemberForm(f => ({ ...f, role: e.target.value }))}>
                        <option value="">(opcional)</option>
                        {farmTypeRoles.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </label>
                    <label className="dv-field">
                      <span>Estado</span>
                      <select value={memberForm.status} onChange={e => setMemberForm(f => ({ ...f, status: e.target.value }))}>
                        <option value="online">Online</option>
                        <option value="offline">Fuera de línea</option>
                        <option value="maintenance">Mantenimiento</option>
                      </select>
                    </label>
                    <label className="dv-field">
                      <span>Prioridad</span>
                      <input type="number" value={memberForm.priority} onChange={e => setMemberForm(f => ({ ...f, priority: e.target.value }))} placeholder="1, 2, 3…" />
                    </label>
                    <label className="dv-field" style={{ gridColumn: '1 / span 2' }}>
                      <span>Notas</span>
                      <input value={memberForm.notes} onChange={e => setMemberForm(f => ({ ...f, notes: e.target.value }))} />
                    </label>
                    <label className="dv-checkbox" style={{ gridColumn: '1 / span 2' }}>
                      <input type="checkbox" checked={memberForm.is_primary} onChange={e => setMemberForm(f => ({ ...f, is_primary: e.target.checked }))} />
                      <span>Es primario</span>
                    </label>
                    <button className="dv-btn dv-btn-primary" type="submit" disabled={memberSaving} style={{ gridColumn: '1 / span 2' }}>
                      {memberSaving ? 'Añadiendo…' : <><PlusIcon /> Añadir miembro</>}
                    </button>
                  </form>
                </div>
              )}
              {!editing && <div className="dv-empty-notes">Pulsa «Editar» para añadir o quitar miembros.</div>}
            </div>
          )}

          {activeTab === 'dependencias' && isEdit && (
            <div className="dv-tab-pane">
              <div className="dv-section">
                <div className="dv-section-title">Servicio padre y dependencias</div>
                <p className="dv-context-help">
                  Este mapa es el del servicio al que pertenece la granja: de dónde depende y a qué afecta.
                </p>
                {form.service_id ? (
                  <>
                    <div className="dv-context-actions" style={{ marginBottom: 10 }}>
                      <Link to={`/services/designer?service=${form.service_id}`} className="dv-btn dv-btn-secondary">
                        Abrir diseñador ↗
                      </Link>
                    </div>
                    <ServiceOverviewMap
                      serviceId={Number(form.service_id)}
                      companyId={companyId}
                      onOpenService={(id) => navigate(`/services?destacar=${id}&abrir=1`)}
                    />
                  </>
                ) : (
                  <div className="dv-empty-state">Selecciona un servicio padre para ver sus dependencias.</div>
                )}
              </div>
            </div>
          )}
        </fieldset>
        <FormDirtyDock
          show={isDirty}
          saving={saving}
          onDiscard={handleDiscard}
          onSave={() => handleSave()}
        />
        </div>
        {/* Panel de actividad y comentarios: uno solo para todas las fichas. */}
        <CiActivityPanel
          companyId={companyId}
          ciType="service_farm"
          ciId={farm?.id}
          tipoLegible="la granja"
        />
      </div>
    </div>
  );
}
